import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { getCurrencySymbol, getWeeklyPrice, formatDurationLabel } from '../../lib/pricing';

export function PricingCard({
  plan,
  currency = 'AUD',
  onSelectPlan,
  ctaHref,
  ctaLabel = 'Get Started',
  ctaDisabled = false,
  ctaLoading = false,
  isCurrent = false,
  isPreselected = false,
}) {
  const hi = plan.highlight;
  const currencySymbol = getCurrencySymbol(currency);
  const weeklyPrice = getWeeklyPrice(plan);
  const totalPrice = plan.price?.toFixed(2);
  const badge = hi ? 'Most Popular' : null;

  const handleClick = () => {
    if (onSelectPlan && !ctaDisabled && !ctaLoading) {
      onSelectPlan(plan);
    }
  };

  const ctaContent = ctaLoading ? (
    <>
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      Processing...
    </>
  ) : isCurrent ? (
    'Current Plan'
  ) : (
    <>
      {ctaLabel}
      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
    </>
  );

  const buttonClassName = `w-full font-semibold font-cabinet rounded-full ${
    hi ? 'bg-white text-[#0A0A0A] hover:bg-white/90' : ''
  }`;

  return (
    <div
      className={`relative rounded-2xl border flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-lg
        ${hi ? 'border-[#0A0A0A] bg-[#0A0A0A] text-white shadow-2xl z-10' : 'border-border bg-card z-0'}
        ${isCurrent || isPreselected ? 'ring-2 ring-primary ring-offset-2' : ''}`}
      data-testid={`pricing-card-${plan.id}`}
    >
      {badge && (
        <div className="absolute -top-4 left-0 right-0 flex justify-center z-20">
          <span className="text-xs font-bold px-4 py-1.5 rounded-full shadow-md font-mono tracking-wide uppercase bg-primary text-white">
            {badge}
          </span>
        </div>
      )}

      <div className={`p-5 flex flex-col gap-4 flex-1 ${badge ? 'pt-7' : 'pt-5'}`}>
        <div>
          <div className={`text-sm font-bold font-cabinet ${hi ? 'text-white/80' : 'text-foreground'}`}>
            {plan.name}
          </div>
          <div className={`text-xs mt-1 font-plex ${hi ? 'text-white/60' : 'text-muted-foreground'}`}>
            {formatDurationLabel(plan.duration_days)}
          </div>
        </div>

        <div>
          <div className="flex items-baseline gap-0.5">
            <span className={`text-3xl font-black font-mono leading-none tracking-tight ${hi ? 'text-white' : 'text-foreground'}`}>
              {currencySymbol}{weeklyPrice}
            </span>
            <span className={`text-sm ml-1 font-plex ${hi ? 'text-white/70' : 'text-muted-foreground'}`}>
              /week
            </span>
          </div>
          <div className={`text-xs mt-1.5 font-plex ${hi ? 'text-white/60' : 'text-muted-foreground'}`}>
            {currencySymbol}{totalPrice} {currency} per billing period
          </div>
        </div>

        <div className={`border-t ${hi ? 'border-white/20' : 'border-border'}`} />

        <ul className="flex flex-col gap-2.5 flex-1">
          {(plan.features || []).map((feature, i) => (
            <li key={i} className="flex items-start gap-2 text-sm font-plex">
              <CheckCircle2 className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${hi ? 'text-green-300' : 'text-success'}`} />
              <span className={`leading-snug ${hi ? 'text-white/90' : ''}`}>{feature}</span>
            </li>
          ))}
        </ul>

        {ctaHref ? (
          <Link to={ctaHref} className="mt-1">
            <Button
              className={buttonClassName}
              variant={hi ? 'default' : 'outline'}
              size="sm"
              data-testid={`pricing-cta-${plan.id}`}
            >
              {ctaContent}
            </Button>
          </Link>
        ) : (
          <Button
            className={`mt-1 ${buttonClassName}`}
            variant={hi ? 'default' : 'outline'}
            size="sm"
            disabled={ctaDisabled || ctaLoading}
            onClick={handleClick}
            data-testid={`pricing-cta-${plan.id}`}
          >
            {ctaContent}
          </Button>
        )}
      </div>
    </div>
  );
}
